import { Button } from "@/src/components/buttons";
import { openDrawer } from "@/src/components/dialogs";
import { Appointment, Exam, Patient } from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import {
  getAge,
  getItemId,
  getName,
  getUserId,
  isMobile,
} from "@/src/utils/utils";
import { useState } from "react";
import { openPatientDashboard } from "../patients/patientDashboard";
import AnapathInfoCompartment from "./anapathInfoCompartment";
import AnapathSamplesCompartment from "./anapathSamplesCompartment";

function AnapathExamDrawer({
  currentExam,
  appointment,
  onChange,
}: {
  currentExam: Exam;
  appointment: Appointment | null | undefined;
  onChange: (exam: Exam) => void;
}) {
  const [exam, setExam] = useState(currentExam);
  if (!exam.patient?.id)
    return <div className="center p20">Veuillez choisir un patient</div>;

  return (
    <div className="c1 mc1 gap15">
      <AnapathInfoCompartment exam={exam} />
      <AnapathSamplesCompartment
        exam={exam}
        patient={exam.patient}
        onDocumentCreate={async (patientDocument) => {
          if (exam.status == "finished") return;
          await apiFetch("appointments", "changeAppointmentStatus", {
            appointmentId: getItemId(appointment),
            status: "resultPendingValidation",
            userId: getUserId(),
          });
          onChange(exam);
        }}
        onDocumentValidate={async (patientDocument) => {
          await apiFetch("appointments", "changeAppointmentStatus", {
            appointmentId: getItemId(appointment),
            status: "finished",
            userId: getUserId(),
          });
          onChange(exam);
        }}
      />
    </div>
  );
}

export async function openAnapathExamDrawer({
  exam,
  onConfirm,
}: {
  exam: Exam;
  onConfirm: (exam: Exam) => void;
}) {
  let fullExam = await apiFetch("exams", "loadFullExam", {
    id: exam.id,
  });
  if (!fullExam) throw new Error("Exam not found");
  exam = fullExam;

  return await openDrawer({
    id: "anapathExamDrawer",
    title: <AnapathExamTitle patient={exam.patient}></AnapathExamTitle>,
    additionalClasses: ["big"],
    content: (
      <AnapathExamDrawer
        appointment={null}
        currentExam={exam}
        onChange={(newExamValue) => {
          exam = newExamValue;
        }}
      />
    ),
    onConfirm: async () => {
      await onConfirm(exam);
    },
  });
}

export async function openAnapathExamDrawerFromAppointment({
  appointment,
  onConfirm,
}: {
  appointment: Appointment;
  onConfirm: (appointment: Appointment) => void;
}) {
  let exam = await apiFetch("appointments", "loadAppointmentItem", {
    appointmentId: appointment.id,
    itemId: getItemId(appointment.item),
  });

  if (!exam) throw new Error("Examen non trouvé");
  if (exam.type != "exam")
    throw new Error(`Type d'examen non valide (${exam.type})`);
  let fullExam = await apiFetch("exams", "loadFullExam", {
    id: exam.id,
  });
  if (!fullExam) throw new Error("Exam not found");
  exam = fullExam;

  return await openDrawer({
    id: "anapathExamDrawer",
    title: <AnapathExamTitle patient={exam.patient}></AnapathExamTitle>,
    additionalClasses: ["big"],
    content: (
      <AnapathExamDrawer
        appointment={appointment}
        currentExam={exam}
        onChange={(newExamValue) => {
          exam = newExamValue;
        }}
      />
    ),
    onConfirm: async () => {
      await onConfirm(appointment);
    },
  });
}

export function AnapathExamTitle({
  patient,
}: {
  patient: Patient | null | undefined;
}) {
  if (!patient) return null;

  return (
    <div className="c1-auto acenter">
      <div className="capitalize">
        <strong>{getName(patient)}</strong>
        <span className="normal gray"> {getAge(patient)}</span>
      </div>
      <Button
        type={"tertiary"}
        onClick={async () => {
          await openPatientDashboard(patient);
        }}
      >
        <span className="f13">
          {isMobile() ? "Dossier" : "Dossier complet"}
        </span>
      </Button>
    </div>
  );
}
